<template>
    <b-container>
        <h4>Your Local Applications</h4>
        <div class="section-line" />
        <b-form-row>
            <b-col md="5" class="my-2">
                <b-input-group prepend="Filter">
                    <b-form-input v-model="filter" placeholder="Type to filter the table." />
                    <b-input-group-append>
                        <b-button :disabled="!filter" @click="filter = ''">
                            Clear
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-col>
            <b-col md="1" />
            <b-col md="5" class="my-2">
                <ay-select v-model="aySelected" :ay-default="isReviewer ? currentAy : null" />
            </b-col>
        </b-form-row>
        <b-table
            id="list-of-local-applications"
            striped
            small
            bordered
            hover
            sort-by="lastUpdate"
            sort-desc
            stacked="md"
            :fields="localApplicationListFields"
            :items="filteredItems"
            caption-top
            :filter="filter"
            @row-clicked="selectLocalApplication"
        >
            <template slot="empty">
                <span>
                    There were no Local Applications found, please create one
                    with the button below or change roles.
                </span>
            </template>

            <template v-slot:cell(applicantName)="row">
                {{ row.item.applicantName }}
            </template>

            <template v-if="isApplicant" v-slot:head(_actions)="field">
                {{ field.label }}
            </template>

            <template
                v-if="isApplicant"
                v-slot:cell(_actions)="row"
                class="actions-column"
            >
                <b-col class="actions-column">
                    <confirmable-submit-action-button
                        :disabled="!row.item._actions.submit"
                        :actionable-item="row.item"
                        :icon-config="{
                            variant: 'submit',
                            classes: ['fa-fw', 'fa-lg']
                        }"
                        :submit-words="{
                            itemType: 'Local Application',
                            action: 'Submit'
                        }"
                        @submitConfirmed="onSubmitConfirm($event)"
                    />
                    <confirmable-delete-action-button
                        :disabled="!row.item._actions.delete"
                        :actionable-item="row.item"
                        :icon-config="{
                            variant: 'delete',
                            classes: ['fa-fw', 'fa-lg']
                        }"
                        :delete-words="{
                            itemType: 'Local Application',
                            action: 'Delete'
                        }"
                        @deleteConfirmed="onDeleteConfirm($event)"
                    />
                    <b-button
                        :href="row.item._actions.printLink"
                        target="_blank"
                        variant="outline-mono-2"
                        size="sm"
                    >
                        <span>
                            <fa-icon
                                v-b-popover.hover.auto="
                                    'Printing this local application'
                                "
                                fixed-width
                                size="lg"
                                :icon="['fal', 'print']"
                            />
                        </span>
                    </b-button>
                </b-col>
            </template>
            <template
                v-else-if="!isApplicant"
                v-slot:cell(_actions)="row"
                class="actions-column"
            >
                <b-col class="actions-column">
                    <b-button
                        :href="row.item._actions.printLink"
                        target="_blank"
                        variant="outline-mono-2"
                        size="sm"
                    >
                        <span>
                            <fa-icon
                                v-b-popover.hover.auto="
                                    'Printing this local application'
                                "
                                fixed-width
                                size="lg"
                                :icon="['fal', 'print']"
                            />
                        </span>
                    </b-button>
                </b-col>
            </template>

            <template slot="table-caption">
                <span>
                    Click a column header to sort the table or click on a row to
                    view/edit the Local Application details.
                </span>
            </template>
        </b-table>

        <div v-if="showCreateButton">
            <b-card :title="localApplicationButtonText">
                <p>
                    Click the button to create your new Local Application.
                    If the table above contains a Local Application for the previous academic year,
                    then the newly created Local Application will be prefilled with the prior AY's data.
                    If there are no Local Applications listed in the table above,
                    clicking the button below will create a new blank Local Application.
                </p>
                <p>
                    Please take note of the new Local Application sections (added AY22) &mdash;
                    <span class="font-weight-bold font-italic">Clusters</span> and
                    <span class="font-weight-bold font-italic">Improvement Plan</span> &mdash;
                    located at the top of the tab list.
                </p>
                <b-button
                    variant="primary"
                    @click="newLocalApplication"
                >
                    {{ localApplicationButtonText }}
                </b-button>
            </b-card>
        </div>
    </b-container>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';

export default {
    name: 'LocalApplicationList',

    metaInfo() {
        return { title: 'Local Applications' };
    },

    data() {
        return {
            localApplicationListFields: [
                { key: 'academicYear', label: 'AY', sortable: true },
                {
                    key: 'localApplicationId',
                    label: 'Local Application ID',
                    sortable: true
                },
                { key: 'applicantName', label: 'Applicant', sortable: true },
                { key: 'applicantId', label: 'FICE', sortable: true },
                { key: 'currentState', label: 'Status', sortable: true },
                {
                    key: 'lastUpdate',
                    label: 'Updated',
                    sortable: true,
                    formatter: value => new Date(value).toLocaleDateString()
                },
                {
                    key: '_actions',
                    label: 'Actions',
                    sortable: false,
                    icons: {
                        submit: false,
                        delete: false,
                        view: false
                    }
                }
            ],

            localApplicationType: null,
            filter: null,
            aySelected: null,
        };
    },

    computed: {
        ...mapGetters({
            userPersona: 'auth/userPersona',
            isAuthorized: 'auth/isAuthorized',
            isApplicant: 'auth/isApplicant',
            isReviewer: 'auth/isReviewer',
            ficeCodeShortName: 'ref/ee/ficeShortName',
            precedingAy: 'ref/global/precedingAy',
            currentAy: 'ref/global/currentAy'
        }),

        ...mapState('localApplication', {
            localApplicationList: 'list',
            localApplicationId: 'localApplicationId'
        }),

        ...mapState('eyprData', {
            eyprDataList: 'eyprDataList'
        }),

        localApplicationListItems() {
            return this.localApplicationList.map(localApplication => {
                return {
                    ...localApplication,
                    localApplicationId: localApplication.localApplicationId,
                    applicantName: this.ficeCodeShortName(
                        localApplication.applicantId
                    ),
                    currentState: localApplication.state
                        ? localApplication.state.currentState
                        : null,
                    lastUpdate: localApplication.state
                        ? localApplication.stateDatetime
                        : null,
                    _actions: {
                        submit: localApplication.state
                            ? this.isAuthorized('submit', localApplication)
                            : false,
                        delete: localApplication.state
                            ? this.isAuthorized('delete', localApplication)
                            : false,
                        view: localApplication.state
                            ? this.isAuthorized('read', localApplication) &&
                                !this.isAuthorized('update', localApplication)
                            : false,
                        printLink:
                            process.env.VUE_APP_API_URL +
                            'print/local_applications/' +
                            localApplication.localApplicationId
                    }
                };
            });
        },

        filteredItems() {
            return (this.aySelected)
                ? this.localApplicationListItems.filter((item) => Number(item.academicYear) === Number(this.aySelected))
                : this.localApplicationListItems;
        },

        showCreateButton() {
            return (
                this.isAuthorized('create', 'local_application') &&
                !this.hasLocalApplicationForCurrentAy &&
                (this.hasEyprDataApprovedForPreviousAy || !this.hasLocalApplicationForPrecedingAy)
            );
        },

        hasEyprDataApprovedForPreviousAy() {
            if (this.eyprDataList) {
                return this.eyprDataList.some(eyprData => {
                    return (
                        eyprData.applicant_id === this.userPersona.applicant_id &&
                        eyprData.academic_year === this.precedingAy &&
                        eyprData.state.current_state === 'APPROVED'
                    );
                });
            }

            return false;
        },

        hasLocalApplicationForCurrentAy() {
            return this.localApplicationList.some(
                localApplication =>
                    localApplication.academicYear === this.currentAy
            );
        },

        hasLocalApplicationForPrecedingAy() {
            return this.localApplicationList.some(
                localApplication =>
                    localApplication.academicYear === this.precedingAy
            );
        },

        localApplicationButtonText() {
            let buttonText = 'Create Local Application For AY' + this.currentAy;

            return buttonText;
        },
    },

    created() {
        this.$store.dispatch('ref/ee/fetchRefEe');
        this.$store.dispatch('localApplication/fetchList');
        this.$store.dispatch('eyprData/fetchEyprDataList');
    },

    methods: {
        selectLocalApplication(row) {
            this.$router.push({
                name: 'localApplication.History',
                params: {
                    localApplicationId: row.localApplicationId,
                }
            });
        },

        ...mapActions({
            createLocalApplication: 'localApplication/create',
            submitLocalApplication: 'localApplication/submit',
            deleteLocalApplication: 'localApplication/delete',
            cloneLocalApplication: 'localApplication/clone'
        }),

        async newLocalApplication() {
            if (!this.hasLocalApplicationForCurrentAy && this.hasLocalApplicationForPrecedingAy) {
                this.newClonedLocalApplication();
            } else {
                this.newBlankLocalApplication();
            }
        },

        async newBlankLocalApplication() {
            await this.createLocalApplication();

            this.$router.push({
                name: 'localApplication.History',
                params: {
                    localApplicationId: this.localApplicationId
                }
            });
            // TODO: Catch local application creation errors
        },

        async newClonedLocalApplication() {
            const sourceLocalAppId = this.getPreviousAyId();

            const localApplication = await this.cloneLocalApplication(sourceLocalAppId);

            this.$router.push({
                name: 'localApplication.History',
                params: {
                    localApplicationId: localApplication.local_application_id,
                    localApplicationAy: localApplication.academic_year
                }
            });
            // TODO: Catch local application clone errors
        },

        // find id of previous AY's Local Application
        getPreviousAyId() {
            const ay = (Number(this.currentAy) - 1) + '';
            const sourceLocalApp = this.localApplicationList.find((lapp) => {
                return lapp.academicYear === ay;
            });

            if (!sourceLocalApp || !sourceLocalApp.localApplicationId) {
                throw new Error('Unable to find ID for previous AY Local Application');
            }

            return sourceLocalApp.localApplicationId;
        },

        onSubmitConfirm(row) {
            this.submitLocalApplication(row.localApplicationId);
            this.$root.$emit('bv::hide::popover');
        },

        onDeleteConfirm(row) {
            this.deleteLocalApplication(row.localApplicationId);
            this.$root.$emit('bv::hide::popover');
        },
    }
};
</script>

<style lang="scss" scoped>
@import "$StyleVars";

h4 {
    text-align: center;
}

h6 {
    text-align: center;
}

td.table-success {
    vertical-align: middle;
    text-align: center;
}

table#list-of-local-applications {
    vertical-align: middle;
    text-align: center;
}

#list-of-local-applications__caption_ {
    font-size: 0.75rem;
}

tr {
    cursor: pointer;
}

$action-button-width: 44.47px;
$estimated-description-column-width: calc(
    83vw - (11ch + 8ch + 22ch + (#{$action-button-width * 3}) + (16px * 3))
);

.table {
    .actions-column .col {
        padding-left: 0;
        padding-right: 0;
        white-space: nowrap;

        .action-placeholder {
            width: $action-button-width;
        }
    }

    /deep/ thead {
        th.description {
            width: calc(100% - (50px + 50px + 80px + 125px));
        }

        th.actions-header,
        th.actions-header-1,
        th.actions-header-2,
        th.actions-header-3,
        th.actions-header-4 {
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            white-space: nowrap;

            .col {
                padding: 5px 9px;

                svg {
                    vertical-align: bottom;
                }
            }
        }

        th.actions-header-1 {
            width: $action-button-width * 1;
        }

        th.actions-header-2 {
            width: $action-button-width * 2;
        }

        th.actions-header-3 {
            width: $action-button-width * 3;
        }

        th.actions-header-4 {
            width: $action-button-width * 4;
        }
    }

    /deep/ tbody {
        tr:hover {
            cursor: pointer;
        }

        td {
            white-space: nowrap;

            .description {
                position: relative;
                width: $estimated-description-column-width;

                &:before {
                    content: attr(title);
                    max-width: 100%;
                    overflow: hidden;
                    position: absolute;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                &:after {
                    content: attr(title);
                    display: inline-block;
                    height: 0;
                    overflow: hidden;
                }
            }
        }
    }
}
</style>
